import { useState, useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import styles from './MessagesAndLikesDonutChart.module.scss';
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { useTranslation } from 'react-i18next';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import {
  DistributionMode,
  DistributionDataResponse,
  getDistributionConfig,
  fetchDistributionData,
  formatDistributionChartData,
} from './distributionChartDataService';
import { showRequestError } from '../../utils/requestError';
import { platformMapping, platformNamesMapping } from '../../utils/platforms';
import { buildDonutOption, getDonutClickIndex, DonutLegendPosition } from '../../utils/echartsDonut';

interface Narrative {
  id: string | number;
}

interface LegendConfig {
  /** 'labeled' draws "Platform: count" callouts next to each slice instead of a legend. */
  position?: DonutLegendPosition;
  textStyle?: {
    fontSize?: number;
    color?: string;
  };
}

const CHART_HEIGHT = 350;

interface MessagesAndLikesDonutChartProps {
  narrative?: Narrative;
  mode: DistributionMode;
  title: string;
  isClickable?: boolean;
  isShare?: boolean;
  colors?: string[];
  legendConfig?: LegendConfig;
}

export const MessagesAndLikesDonutChart: React.FC<MessagesAndLikesDonutChartProps> = ({
  narrative,
  mode,
  title,
  isClickable = true,
  isShare = false,
  colors = [
    '#0043CE',
    '#0F62FE',
    '#4589FF',
    '#78A9FF',
    '#A6C8FF',
    '#D0E2FF',
    '#EDF5FF',
    '#002D9C',
  ],
  legendConfig = {
    position: 'labeled',
    textStyle: { fontSize: 14 },
  },
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<ReactECharts>(null);

  const [chartData, setChartData] = useState<DistributionDataResponse | null>(null);
  const [modal, setModal] = useState({ isVisible: false, platform: '', title: '' });
  const [addToCaseModal, setAddToCaseModal] = useState({
    isOpen: false,
    platform: '',
    sources: null as string | number | null,
  });
  const [addToGroupModal, setAddToGroupModal] = useState({
    isOpen: false,
    platform: '',
    sources: null as string | number | null,
  });

  const config = useMemo(() => getDistributionConfig(mode), [mode]);

  const getData = async () => {
    if (!narrative?.id) return;

    try {
      const data = await fetchDistributionData(narrative.id, mode, isShare);
      setChartData(data);
    } catch (error) {
      showRequestError(error as Error, t('Platform distribution'));
      setChartData({} as DistributionDataResponse);
    }
  };

  useEffect(() => {
    if (narrative?.id) {
      getData();
    }
  }, [narrative?.id, mode]);

  const pieData = useMemo(
    () => formatDistributionChartData(chartData, config),
    [chartData, config],
  );

  const legendPosition = legendConfig.position ?? 'labeled';
  const labelFontSize = legendConfig.textStyle?.fontSize ?? 14;
  const labelColor = legendConfig.textStyle?.color;

  const option = useMemo(
    () =>
      buildDonutOption({
        data: pieData,
        colors,
        legendPosition,
        labelFontSize,
        labelColor,
        isClickable,
        seriesName: t(config.chartLabel),
        getChartWidth: () => chartRef.current?.getEchartsInstance()?.getWidth() ?? 0,
      }),
    [pieData, colors, legendPosition, labelFontSize, labelColor, isClickable, config, t],
  );

  const onEvents = useMemo(() => {
    if (!isClickable) return undefined;
    return {
      click: (params: any) => {
        const index = getDonutClickIndex(params, chartRef.current?.getEchartsInstance());
        const platform = index !== null ? pieData[index]?.platform : undefined;
        if (!platform) return;

        setModal({
          isVisible: true,
          platform,
          title: platformNamesMapping[platform] || platform,
        });
      },
    };
  }, [isClickable, pieData]);

  if (chartData === null) {
    return (
      <div className={styles.widgetContainer}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.container}>
          <div className={styles.loaderWrapper}>
            <LoaderSmall />
          </div>
        </div>
      </div>
    );
  }

  if (!chartData?.objects || chartData?.objects.length === 0) {
    return (
      <div className={styles.widgetContainer}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.container}>
          <div className={styles.noData}>{t('There is no data to display')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.widgetContainer}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.container}>
        <div className={styles.chartWrapper}>
          <div className={styles.chart}>
            <ReactECharts
              ref={chartRef}
              option={option}
              notMerge
              style={{ width: '100%', height: `${CHART_HEIGHT}px` }}
              opts={{ renderer: 'svg' }}
              onEvents={onEvents}
            />
          </div>
        </div>
      </div>
      {modal.isVisible && (
        <WidgetContentModal
          infoModal={modal.isVisible}
          isChart={true}
          title={modal.title || ''}
          narrative={narrative}
          widgetName={config.widgetName}
          isShare={isShare}
          showAddToActions={true}
          onChange={(bool: boolean) => {
            setModal({
              isVisible: bool,
              platform: '',
              title: '',
            });
          }}
          onAddToCase={(bool: boolean, sourceId: number | string| undefined) => {
            setAddToCaseModal({
              isOpen: bool,
              platform: modal.platform,
              sources: sourceId ? sourceId : null,
            });
          }}
          onAddToGroup={(bool: boolean, sourceId: number | string | null | undefined) => {
            setAddToGroupModal({
              isOpen: bool,
              platform: modal.platform,
              sources: sourceId ? sourceId : null,
            });
          }}
          tabIndex={0}
          type={null}
          subType={null}
          platformOption={modal.platform ? [{ value: modal.platform }] : null}
          platformsOptions={null}
          stateAffiliatedId={null}
          stateAffiliatedExclude={false}
          affiliationTypes={null}
          affiliationTypesExclude={false}
          highRiskExclude={false}
          botsExclude={false}
          countryId={null}
          audienceRange={null}
          sourceGroup={null}
          sourceGroupExclude={false}
          source={null}
          language={null}
          sentimentOptions={null}
          eventIdsOptions={null}
          opinionIdsOptions={null}
          startDate={null}
          endDate={null}
          contentTypeOptions={null}
          opinion={null}
          countryIdExclude={false}
          languageCodeExclude={false}
          event={null}
        />
      )}

      {addToCaseModal.isOpen && (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={narrative?.id}
          areAllMessagesSelected={true}
          platforms={[platformMapping[addToCaseModal.platform]]}
          onChange={(bool: boolean) => {
            setAddToCaseModal({
              isOpen: bool,
              platform: '',
              sources: null,
            });
          }}
          messages={null}
          isSearch={false}
          searchQuery={''}
          messagesStatus={null}
          showOnlyFavorites={null}
          startDate={null}
          endDate={null}
          sourceGroups={null}
          languages={null}
          sources={addToCaseModal.sources ? [addToCaseModal.sources] : null}
          countries={null}
          sentiment={null}
          contentTypes={null}
          showOnlyBots={null}
          discreditations={null}
          affiliationCountries={null}
          affiliationTypes={null}
          affiliationTypesExclude={null}
          highRiskExclude={null}
          botsExclude={null}
          botsType={null}
          feeds={null}
          geoGroup={null}
          audienceRange={null}
          isVerified={null}
          suspendedByPlatform={null}
          immatureAccount={null}
          languagesExclude={null}
          countriesExclude={null}
          sourceGroupsExclude={null}
          sourcesExclude={null}
          affiliationCountriesExclude={null}
          feedsExclude={null}
          geoGroupExclude={null}
          eventIds={null}
          opinionIds={null}
          widgetName={config.widgetName}
        />
      )}

      {addToGroupModal.isOpen && (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          platforms={[platformMapping[addToGroupModal.platform]]}
          narrative={narrative}
          onChange={(bool: boolean) => {
            setAddToGroupModal({
              isOpen: bool,
              platform: '',
              sources: null,
            });
          }}
          allMessages={null}
          messages={null}
          sources={null}
          isSearch={false}
          searchQuery={''}
          messagesStatus={null}
          showOnlyFavorites={null}
          startDate={null}
          endDate={null}
          sourceGroups={null}
          languages={null}
          sourcesFilter={addToGroupModal.sources ? [addToGroupModal.sources] : null}
          countries={null}
          sentiment={null}
          contentTypes={null}
          showOnlyBots={null}
          discreditations={null}
          affiliationCountries={null}
          affiliationTypes={null}
          affiliationTypesExclude={null}
          highRiskExclude={null}
          botsExclude={null}
          botsType={null}
          feeds={null}
          geoGroup={null}
          audienceRange={null}
          isVerified={null}
          suspendedByPlatform={null}
          immatureAccount={null}
          languagesExclude={null}
          countriesExclude={null}
          sourceGroupsExclude={null}
          sourcesExclude={null}
          affiliationCountriesExclude={null}
          feedsExclude={null}
          geoGroupExclude={null}
          eventIds={null}
          opinionIds={null}
          widgetName={config.widgetName}
        />
      )}
    </div>
  );
};
